import React, { useState, useEffect } from 'react';
import { Card, Slider, Button, Space, Typography, message, Row, Col, Progress, Divider } from 'antd';
import { TrophyOutlined, StarFilled, DashboardOutlined } from '@ant-design/icons';
import { getContents, submitScores } from '../../api/learn';

const { Title, Text } = Typography;

const Step4Score = ({ onComplete }) => {
  const [contents, setContents] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    try {
      const response = await getContents();
      if (response.code === 200) {
        const initialScores = {};
        response.data.contents.forEach(item => {
          initialScores[item.item_number] = 5.0;
        });
        setScores(initialScores);
        setContents(response.data.contents);
      }
    } catch (error) {
      message.error('加载内容失败');
    }
  };

  const handleScoreChange = (itemNumber, value) => {
    const roundedValue = Math.round(value * 10) / 10;
    setScores({ ...scores, [itemNumber]: roundedValue });
  };

  const calculateTotal = () => {
    return Object.values(scores).reduce((sum, score) => sum + (score || 0), 0);
  };

  const calculateAverage = () => {
    if (contents.length === 0) return '0.0';
    const total = calculateTotal();
    return (total / contents.length).toFixed(1);
  };

  const getScoreDistribution = () => {
    const scoreValues = Object.values(scores);
    if (scoreValues.length === 0) {
      return { low: 0, mid: 0, high: 0, excellent: 0 };
    }
    
    const low = scoreValues.filter(s => s <= 3).length;
    const mid = scoreValues.filter(s => s > 3 && s <= 6).length;
    const high = scoreValues.filter(s => s > 6 && s <= 8).length;
    const excellent = scoreValues.filter(s => s > 8).length;
    
    const total = scoreValues.length;
    return {
      low: Math.round((low / total) * 100),
      mid: Math.round((mid / total) * 100),
      high: Math.round((high / total) * 100),
      excellent: Math.round((excellent / total) * 100)
    };
  };

  const handleSubmit = async () => {
    if (Object.keys(scores).length !== contents.length) {
      message.warning('请对所有准则进行评分');
      return;
    }

    try {
      setLoading(true);
      
      const scoresArray = contents.map(item => ({
        item_number: item.item_number,
        score: scores[item.item_number]
      }));

      const response = await submitScores(scoresArray);
      
      if (response.code === 200) {
        message.success(`评分提交成功!总分:${response.data.total_score}分,平均分:${response.data.avg_score}分`);
        setTimeout(() => {
          onComplete();
        }, 1500);
      }
    } catch (error) {
      message.error(error.message || '提交失败');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (!score || score <= 3) return '#ff4d4f';
    if (score <= 6) return '#faad14';
    if (score <= 8) return '#52c41a';
    return '#1890ff';
  };

  const getScoreLabel = (score) => {
    if (!score || score <= 3) return '较差';
    if (score <= 6) return '一般';
    if (score <= 8) return '良好';
    return '优秀';
  };

  // 样式对象
  const styles = {
    statisticsCard: {
      background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      marginBottom: 24
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    statIcon: {
      width: '64px',
      height: '64px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    statLabel: {
      fontSize: '14px',
      color: '#8c8c8c',
      marginBottom: '4px'
    },
    statSuffix: {
      fontSize: '14px',
      color: '#8c8c8c'
    },
    statBadge: {
      fontSize: '12px',
      fontWeight: 500,
      marginTop: '4px'
    },
    barItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '4px'
    },
    barLabel: {
      fontSize: '12px',
      width: '40px',
      color: '#595959'
    },
    scoreCard: (score) => ({
      marginBottom: 16,
      borderLeft: `4px solid ${getScoreColor(score)}`,
      transition: 'all 0.3s'
    }),
    scoreNumber: {
      fontSize: '12px',
      color: '#8c8c8c',
      marginBottom: '4px'
    },
    scoreTitle: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#262626',
      marginBottom: '8px'
    },
    scoreContent: {
      fontSize: '14px',
      color: '#595959',
      lineHeight: 1.6
    },
    scoreBig: {
      fontSize: '48px',
      fontWeight: 'bold',
      lineHeight: 1
    },
    scoreLabelStyle: {
      fontSize: '16px',
      fontWeight: 500
    },
    scoreHints: {
      marginTop: '8px',
      textAlign: 'center'
    }
  };

  const distribution = getScoreDistribution();
  const avgScore = parseFloat(calculateAverage());
  const totalScore = calculateTotal();

  return (
    <div className="step-score">
      <div className="step-header">
        <Title level={3}>步骤四:评分反馈</Title>
        <Text type="secondary">请对公司在六项行为准则方面的现状进行评分(1-10分,支持0.5分)</Text>
      </div>

      {/* 统计卡片 */}
      <Card style={styles.statisticsCard}>
        <Row gutter={[24, 16]}>
          {/* 总分 */}
          <Col xs={24} sm={8}>
            <div style={styles.statItem}>
              <div style={{ ...styles.statIcon, background: '#e6f7ff' }}>
                <TrophyOutlined style={{ fontSize: 32, color: '#1890ff' }} />
              </div>
              <div>
                <div style={styles.statLabel}>总分</div>
                <div>
                  <span style={{ fontSize: 36, fontWeight: 'bold', color: '#1890ff' }}>
                    {totalScore.toFixed(1)}
                  </span>
                  <span style={styles.statSuffix}>/ {contents.length * 10}</span>
                </div>
              </div>
            </div>
          </Col>

          {/* 平均分 */}
          <Col xs={24} sm={8}>
            <div style={styles.statItem}>
              <div style={{ ...styles.statIcon, background: '#f6ffed' }}>
                <StarFilled style={{ fontSize: 32, color: '#52c41a' }} />
              </div>
              <div>
                <div style={styles.statLabel}>平均分</div>
                <div>
                  <span style={{ fontSize: 36, fontWeight: 'bold', color: '#52c41a' }}>
                    {avgScore}
                  </span>
                  <span style={styles.statSuffix}>/ 10</span>
                </div>
                <div style={{ ...styles.statBadge, color: getScoreColor(avgScore) }}>
                  {getScoreLabel(avgScore)}
                </div>
              </div>
            </div>
          </Col>

          {/* 分布 */}
          <Col xs={24} sm={8}>
            <div style={styles.statItem}>
              <div style={{ ...styles.statIcon, background: '#fff7e6' }}>
                <DashboardOutlined style={{ fontSize: 32, color: '#faad14' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.statLabel}>评分分布</div>
                <div style={{ marginTop: '8px' }}>
                  <div style={styles.barItem}>
                    <span style={styles.barLabel}>优秀</span>
                    <Progress 
                      percent={distribution.excellent} 
                      size="small" 
                      showInfo={false}
                      strokeColor="#1890ff"
                    />
                  </div>
                  <div style={styles.barItem}>
                    <span style={styles.barLabel}>良好</span>
                    <Progress 
                      percent={distribution.high} 
                      size="small" 
                      showInfo={false}
                      strokeColor="#52c41a"
                    />
                  </div>
                  <div style={styles.barItem}>
                    <span style={styles.barLabel}>一般</span>
                    <Progress 
                      percent={distribution.mid} 
                      size="small" 
                      showInfo={false}
                      strokeColor="#faad14"
                    />
                  </div>
                  <div style={styles.barItem}>
                    <span style={styles.barLabel}>较差</span>
                    <Progress 
                      percent={distribution.low} 
                      size="small" 
                      showInfo={false}
                      strokeColor="#ff4d4f"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Divider>各准则评分</Divider>

      {/* 评分卡片 */}
      {contents.map(item => {
        const score = scores[item.item_number] || 5.0;
        
        return (
          <Card 
            key={item.item_number}
            style={styles.scoreCard(score)}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <div>
                  <div style={styles.scoreNumber}>准则 {item.item_number}</div>
                  <div style={styles.scoreTitle}>{item.title}</div>
                  <div style={styles.scoreContent}>{item.content}</div>
                </div>
              </Col>
              
              <Col xs={24} md={16}>
                <div style={{ padding: '8px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ ...styles.scoreBig, color: getScoreColor(score) }}>
                      {score.toFixed(1)}
                    </div>
                    <div style={{ ...styles.scoreLabelStyle, color: getScoreColor(score) }}>
                      {getScoreLabel(score)}
                    </div>
                  </div>
                  
                  <Slider
                    min={1}
                    max={10}
                    step={0.5}
                    value={score}
                    onChange={(value) => handleScoreChange(item.item_number, value)}
                    marks={{
                      1: '1',
                      2: '2',
                      3: '3',
                      4: '4',
                      5: '5',
                      6: '6',
                      7: '7',
                      8: '8',
                      9: '9',
                      10: '10'
                    }}
                    tooltip={{
                      formatter: (value) => value ? `${value.toFixed(1)} 分` : ''
                    }}
                  />
                  
                  <div style={styles.scoreHints}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      1-3分:较差 | 4-6分:一般 | 7-8分:良好 | 9-10分:优秀
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        );
      })}

      {/* 提交按钮 */}
      <Button
        type="primary"
        size="large"
        onClick={handleSubmit}
        loading={loading}
        block
        style={{ marginTop: 24, height: 48, fontSize: 16 }}
      >
        提交评分
      </Button>
    </div>
  );
};

export default Step4Score;
